import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { TextInput } from '@dojo/widgets/text-input';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { throttle } from 'lodash-es';
import { createNlsMiddleware, lccstate, keymap, pathmap, searchOpen } from 'lcc-state';
import { publish } from 'pub-sub';
import { css } from '../css/search.m.css';
import { svc } from '../support/searchsvc';
import { Keys } from '@holmescorp/common';
const nls = createNlsMiddleware('settings');
const factory = create({
    icache: createICacheMiddleware(),
    theme,
    lccstate,
    nls
});
export const Search = factory(({ middleware: { icache, lccstate, theme, nls } }) => {
    const { isPlaceholder, messages } = nls();
    if (isPlaceholder) {
        return null;
    }
    const themeCss = theme.classes(css);
    const setKeywords = throttle(async () => {
        const find = icache.getOrSet('currentInput', '');
        if (find.length > 0) {
            const language = window.hcstatic.portal_language || 'en';
            const { get, path } = lccstate;
            const version = get(path('bookversion'));
            const kwlist = await svc.keyword_search({ find, version, language });
            icache.set('keywords', kwlist.map((kw) => {
                return v('li', {
                    tabIndex: 0,
                    onclick: (event) => {
                        event && event.stopPropagation();
                        icache.set('showingKeywords', false);
                        icache.set('selected', kw);
                        icache.set('topics', []);
                        topic_list();
                    },
                    onkeydown: (event) => {
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                icache.set('showingKeywords', false);
                                icache.set('selected', kw);
                                icache.set('topics', []);
                                topic_list();
                                break;
                        }
                    }
                }, [kw]);
            }));
        }
        else {
            icache.set('keywords', []);
        }
    }, 1100, { trailing: true });
    const keyword_list = () => {
        setKeywords();
    };
    const topic_list = async () => {
        const language = window.hcstatic.portal_language || 'en';
        const { get, path } = lccstate;
        const version = get(path('bookversion'));
        const topicids = await svc.topics_for_keyword({
            keyword: icache.getOrSet('selected', ''),
            version,
            language
        });
        icache.set('topics', topicids.map((topicid) => {
            const km = keymap.get(topicid);
            const pm = pathmap.get(km.path);
            const { topicref: topic } = pm.topic;
            return w('TopicRef', {
                topic,
                messages,
                action: () => {
                    searchOpen({ searchOpen: false });
                    publish('close-all/panes');
                }
            });
        }));
    };
    const render_topics = () => {
        return v('fieldset', { key: 'search-topics', classes: [themeCss.resultSet] }, [
            v('legend', { classes: [themeCss.resultHead] }, [icache.get('selected')]),
            v('div', { classes: [themeCss.topicList] }, icache.get('topics'))
        ]);
    };
    const showing = icache.get('showingKeywords');
    return v('div', { xqa: 'search wrapper', classes: [themeCss.root] }, [
        v('p', { xqa: 'search-instructions', classes: [themeCss.instructions] }, [messages['search_instructions']]),
        v('div', { classes: [themeCss.inputWrapper] }, [
            w(TextInput, {
                key: 'search-inp',
                labelHidden: true,
                type: 'text',
                value: icache.get('currentInput'),
                onValue: (value) => {
                    icache.set('currentInput', value);
                    icache.set('showingKeywords', value !== '');
                    keyword_list();
                }
            }, [{ label: messages.search_title }]),
            v('span', {
                tabIndex: 0,
                role: 'button',
                ...formatAriaProperties({
                    label: 'expand-list',
                    expanded: showing ? showing.toString() : 'false'
                }),
                classes: [
                    themeCss.listButton,
                    icache.get('showingKeywords') ? themeCss.listActive : themeCss.listInactive
                ],
                onclick: (event) => {
                    event && event.stopPropagation();
                    const current = icache.get('currentInput');
                    if (current) {
                        icache.set('showingKeywords', !showing);
                    }
                },
                onkeydown: (event) => {
                    const current = icache.get('currentInput');
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            if (current) {
                                icache.set('showingKeywords', !showing);
                            }
                            break;
                    }
                }
            }, [
                v('i', {
                    classes: ['fas', icache.get('showingKeywords') ? 'fa-chevron-down' : 'fa-chevron-left']
                })
            ])
        ]),
        icache.get('showingKeywords')
            ? v('ul', { key: 'search-keywords', xqa: 'search keywords', classes: [css.searchResults] }, icache.get('keywords'))
            : render_topics()
    ]);
});
